<script setup lang="ts">
    import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
    import { RouterLink, useRoute } from 'vue-router';
    import { useDashboardStore } from '@/js/stores/dashboard';

    const dashboard = useDashboardStore();
    const route = useRoute();

    const containerRef = ref<HTMLElement | null>(null);
    const measureRef = ref<HTMLElement | null>(null);

    const containerWidth = ref(0);
    const itemWidths = ref<Map<string, number>>(new Map());

    // The gap between tabs, in pixels. This is used to calculate how many tabs can fit in the available width.
    const TAB_GAP = 20;

    const activeId = computed(() => route.query.id?.toString());
    const favorites = computed(() => dashboard.items.filter((item) => item.isFavorite));

    const visibleFavorites = computed(() => {
        const list = favorites.value;
        if (list.length === 0) return [];
        if (containerWidth.value === 0 || itemWidths.value.size === 0) return list;

        const available = containerWidth.value;
        if (available <= 0) return [];

        let used = 0;
        let prefixEnd = 0;
        for (let i = 0; i < list.length; i++) {
            const width = itemWidths.value.get(list[i].id) ?? 0;
            const next = used + (i === 0 ? width : width + TAB_GAP);
            if (next > available) break;
            used = next;
            prefixEnd = i + 1;
        }

        const activeIndex = list.findIndex((item) => item.id === activeId.value);
        if (activeIndex === -1 || activeIndex < prefixEnd) {
            return list.slice(0, prefixEnd);
        }

        const activeWidth = itemWidths.value.get(list[activeIndex].id) ?? 0;
        let rightUsed = activeWidth;
        let startIndex = activeIndex;
        for (let i = activeIndex - 1; i >= 0; i--) {
            const width = itemWidths.value.get(list[i].id) ?? 0;
            if (rightUsed + width + TAB_GAP > available) break;
            rightUsed += width + TAB_GAP;
            startIndex = i;
        }
        return list.slice(startIndex, activeIndex + 1);
    });

    const measure = async () => {
        await nextTick();
        containerWidth.value = containerRef.value?.offsetWidth ?? 0;

        if (!measureRef.value) return;
        const widths = new Map<string, number>();
        for (const child of Array.from(measureRef.value.children) as HTMLElement[]) {
            const id = child.dataset.id;
            if (id) widths.set(id, child.offsetWidth);
        }
        itemWidths.value = widths;
    };

    let resizeObserver: ResizeObserver | null = null;

    onMounted(() => {
        resizeObserver = new ResizeObserver(() => measure());
        if (containerRef.value) resizeObserver.observe(containerRef.value);
        measure();
    });

    onBeforeUnmount(() => {
        resizeObserver?.disconnect();
        resizeObserver = null;
    });

    watch(favorites, () => measure());
    watch(activeId, () => measure());
</script>

<template>
    <nav
        v-if="visibleFavorites.length > 0"
        ref="containerRef"
        class="relative flex items-center w-full min-w-0 border-b border-gray-lightest pb-3 mb-6"
    >
        <ul class="flex flex-1 min-w-0 items-center gap-5 overflow-hidden">
            <li v-for="db in visibleFavorites" :key="db.id">
                <RouterLink
                    :to="{ query: { ...route.query, id: db.id } }"
                    class="block px-3 py-2 custom-focus whitespace-nowrap rounded-sm text-sm transition-colors hover:bg-gray-lightest focus-visible:bg-gray-lightest"
                    :class="db.id === activeId ? 'bg-gray-lightest ' : ''"
                    :aria-current="db.id === activeId ? 'page' : undefined"
                >
                    {{ db.title }}
                </RouterLink>
            </li>
        </ul>
        <div class="overflow-hidden w-full absolute">
            <ul
                ref="measureRef"
                aria-hidden="true"
                class="absolute top-0 left-0 -translate-y-full flex items-center gap-5 invisible pointer-events-none"
            >
                <li v-for="db in favorites" :key="`measure-${db.id}`" :data-id="db.id">
                    <button
                        type="button"
                        class="block px-3 py-2 whitespace-nowrap rounded-sm text-sm transition-colors hover:bg-gray-lightest focus-visible:bg-gray-lightest"
                    >
                        {{ db.title }}
                    </button>
                </li>
            </ul>
        </div>
    </nav>
</template>
